import { useCallback, useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { isMarketOpen } from "@/lib/marketHours";
import type { AnalysisDetail, Row, TrackerSnapshot } from "@/lib/api";
import {
  atrClass,
  dropClass,
  hitClass,
  posClass,
  retClass,
  riskClass,
  rrClass,
  rsClass,
  tickerBold,
  tickerTier,
  tierClass,
  trendClass,
  TREND_GLYPHS,
} from "@/lib/colors";
import { dollarCompact, money, percent, price, ratio, signedPercent } from "@/lib/format";
import { ChartPanel, type SignalKey } from "./ChartPanel";

interface TickerDetailProps {
  ticker: string | null;
  row: Row | undefined;
  detail: AnalysisDetail | undefined;
  snapshot: TrackerSnapshot | undefined;
  pollIntervalSec: number;
  onClose: () => void;
}

const MIN_DETAIL_WIDTH = 520;
const MAX_DETAIL_WIDTH_RATIO = 0.98;
const DEFAULT_DETAIL_WIDTH_RATIO = 0.62;
const DETAIL_WIDTH_STORAGE_KEY = "taco.detailWidth";

function clampDetailWidth(w: number): number {
  if (typeof window === "undefined") return w;
  const vw = window.innerWidth;
  const maxW = Math.max(MIN_DETAIL_WIDTH, Math.floor(vw * MAX_DETAIL_WIDTH_RATIO));
  return Math.max(MIN_DETAIL_WIDTH, Math.min(w, maxW));
}

function initialDetailWidth(): number {
  if (typeof window === "undefined") return 960;
  const raw = window.localStorage.getItem(DETAIL_WIDTH_STORAGE_KEY);
  const stored = raw ? Number.parseInt(raw, 10) : NaN;
  if (Number.isFinite(stored) && stored > 0) return clampDetailWidth(stored);
  return clampDetailWidth(Math.floor(window.innerWidth * DEFAULT_DETAIL_WIDTH_RATIO));
}

function useResizableDetailWidth() {
  const [width, setWidth] = useState<number>(() => initialDetailWidth());
  const widthRef = useRef(width);
  widthRef.current = width;

  // Keep width sane when the user resizes the browser window.
  useEffect(() => {
    const onResize = () => setWidth((w) => clampDetailWidth(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persist the latest committed width to localStorage.
  useEffect(() => {
    try {
      window.localStorage.setItem(DETAIL_WIDTH_STORAGE_KEY, String(width));
    } catch {
      /* storage might be unavailable (private mode); fine to ignore */
    }
  }, [width]);

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startWidth = widthRef.current;
      const prevBodySelect = document.body.style.userSelect;
      document.body.style.userSelect = "none";

      const move = (ev: PointerEvent) => {
        // Sheet is anchored to the right edge, so dragging LEFT widens it.
        const delta = startX - ev.clientX;
        setWidth(clampDetailWidth(startWidth + delta));
      };
      const release = () => {
        handle.releasePointerCapture(e.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", release);
        handle.removeEventListener("pointercancel", release);
        document.body.style.userSelect = prevBodySelect;
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", release);
      handle.addEventListener("pointercancel", release);
    },
    []
  );

  const onResizeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Keyboard accessibility: arrow keys nudge by 24 px.
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setWidth((w) => clampDetailWidth(w + 24));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setWidth((w) => clampDetailWidth(w - 24));
      }
    },
    []
  );

  return { width, onResizePointerDown, onResizeKeyDown };
}

export function TickerDetail({ ticker, row, detail, snapshot, pollIntervalSec, onClose }: TickerDetailProps) {
  const open = ticker !== null;
  const [activeSignal, setActiveSignal] = useState<SignalKey | null>(null);
  const { width, onResizePointerDown, onResizeKeyDown } = useResizableDetailWidth();
  // Clear the overlay whenever the user switches tickers so a stale
  // selection doesn't leak across different detail views.
  useEffect(() => {
    setActiveSignal(null);
  }, [ticker]);
  const toggleSignal = (key: SignalKey) =>
    setActiveSignal((prev) => (prev === key ? null : key));

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        className="!max-w-none w-full overflow-hidden flex flex-col"
        style={{ width: `${width}px` }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          tabIndex={0}
          onPointerDown={onResizePointerDown}
          onKeyDown={onResizeKeyDown}
          className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-ring/40 focus-visible:bg-ring/50 focus-visible:outline-none transition-colors z-20"
        />
        {ticker && row && detail ? (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-3">
                <span
                  className={cn(
                    "font-mono text-3xl",
                    tierClass(tickerTier(row.overall_score, row.hit_rate)),
                    tickerBold(row.overall_score, row.hit_rate) && "font-bold"
                  )}
                >
                  {ticker}
                </span>
                <span className="text-2xl font-mono text-muted-foreground">
                  <span className="text-xs align-top mr-1 uppercase tracking-wide">
                    close
                  </span>
                  ${price(row.current_price)}
                </span>
                <span
                  className={cn(
                    "text-2xl font-mono",
                    trendClass(row.trend)
                  )}
                >
                  {TREND_GLYPHS[row.trend]}
                </span>
                <span className="ml-auto text-sm font-normal text-muted-foreground">
                  score {row.overall_score}/7 · struct {row.structural_score}/4
                </span>
              </SheetTitle>
              <SheetDescription>
                {detail.benchmark ? `benchmark ${detail.benchmark}` : "no benchmark"} ·{" "}
                support ${price(detail.support)} · resistance ${price(detail.resistance)}
              </SheetDescription>
            </SheetHeader>
            <CompanyHeader row={row} />
            <Separator className="my-3" />
            {/* min-h-0 is load-bearing: without it, a flex child with
              * flex-1 inherits min-height: auto, which refuses to
              * shrink below the child's content height. The ScrollArea
              * then grows past the SheetContent and its overflow-hidden
              * just clips instead of scrolling. */}
            <ScrollArea className="flex-1 min-h-0 pr-3">
              <div className="space-y-4 pb-6">
                <ChartPanel
                  detail={detail}
                  row={row}
                  activeSignal={activeSignal}
                />
                <LiveTrackerCard
                  row={row}
                  snapshot={snapshot}
                  pollIntervalSec={pollIntervalSec}
                />
                <QualityFlagsCard row={row} />
                <FundamentalsCard row={row} />
                <ScoreBreakdown
                  row={row}
                  detail={detail}
                  activeSignal={activeSignal}
                  onToggle={toggleSignal}
                />
                <RiskCard row={row} />
                <SwingList detail={detail} />
              </div>
            </ScrollArea>
          </>
        ) : (
          <SheetHeader>
            <SheetTitle>Loading…</SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** CompanyHeader renders the expanded Security Name, sector/industry
 *  badges, and an expandable business description. Rendered between
 *  the SheetHeader and the detail content when the server supplied
 *  any metadata — the Row fields are all optional, so a run without a
 *  metadata provider (CLI-only mode, test harness, or a ticker Yahoo
 *  doesn't know) just omits this block and the sheet looks the same
 *  as before the feature landed. Description is clamped to three
 *  lines by default with a "more / less" toggle so a ten-paragraph
 *  10-K-style summary doesn't push the chart below the fold.
 */
function CompanyHeader({ row }: { row: Row }) {
  const [expanded, setExpanded] = useState(false);
  const { security_name, sector, industry, description } = row;
  if (!security_name && !sector && !industry && !description) {
    return null;
  }
  return (
    <div className="mt-2 space-y-2">
      {(security_name || sector || industry) && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {security_name && (
            <span className="font-medium text-foreground">{security_name}</span>
          )}
          {sector && <Badge variant="secondary">{sector}</Badge>}
          {industry && <span className="text-xs">{industry}</span>}
        </div>
      )}
      {description && (
        <div className="text-sm leading-snug text-muted-foreground">
          <p className={cn(!expanded && "line-clamp-3")}>{description}</p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-xs text-primary hover:underline"
          >
            {expanded ? "less" : "more"}
          </button>
        </div>
      )}
    </div>
  );
}

interface SignalCell {
  key: SignalKey;
  label: string;
  value: string;
  className: string;
  contribution: number | "—";
  note?: string;
}

function ScoreBreakdown({
  row,
  detail,
  activeSignal,
  onToggle,
}: {
  row: Row;
  detail: AnalysisDetail;
  activeSignal: SignalKey | null;
  onToggle: (key: SignalKey) => void;
}) {
  const trendContribution = row.trend === "up" ? 1 : row.trend === "down" ? -1 : 0;

  const signals: SignalCell[] = [
    // Structure: hit, atr, trend, rs
    {
      key: "hit",
      label: "hit",
      value: percent(row.hit_rate),
      className: hitClass(row.hit_rate),
      contribution: signalContrib(row.hit_rate, 0.6, 0.4, true),
      note: row.hit_rate === 0 ? "concrete 0 vetoes green verdict" : undefined,
    },
    {
      key: "atr",
      label: "atr %",
      value: percent(row.atr_pct, 1),
      className: atrClass(row.atr_pct),
      contribution: signalContrib(row.atr_pct, 0.03, 0.05, false),
    },
    {
      key: "trend",
      label: "trend",
      value: TREND_GLYPHS[row.trend],
      className: trendClass(row.trend),
      contribution: trendContribution,
      note: row.trend === "unknown" ? "too few bars for SMA slope" : undefined,
    },
    {
      key: "rs",
      label: "rs",
      value: signedPercent(row.relative_strength),
      className: rsClass(row.relative_strength),
      contribution: signalContrib(row.relative_strength, 0.05, -0.05, true),
      note: row.benchmark ? `vs ${row.benchmark}` : undefined,
    },
    // Position: ret, pos
    {
      key: "ret",
      label: "ret %",
      value: percent(row.expected_return_pct, 1),
      className: retClass(row.expected_return_pct),
      contribution: signalContrib(row.expected_return_pct, 0.15, 0.08, true),
    },
    {
      key: "pos",
      label: "pos",
      value: percent(row.current_position),
      className: posClass(row.current_position),
      contribution: signalContrib(row.current_position, 0.25, 0.5, false),
    },
    // Risk: R:R
    {
      key: "rr",
      label: "R:R",
      value: ratio(row.reward_risk),
      className: rrClass(row.reward_risk),
      contribution: signalContrib(row.reward_risk, 3.0, 2.0, true),
    },
  ];

  return (
    <section className="rounded-lg border p-4">
      <h3 className="text-sm font-semibold mb-3">Score breakdown</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {signals.map((s) => {
          const active = activeSignal === s.key;
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(s.key)}
              className={cn(
                "text-left rounded border p-3 bg-background transition-colors",
                "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active && "ring-2 ring-ring bg-muted/60"
              )}
            >
              <div className="text-xs text-muted-foreground">{s.label}</div>
              <div className={cn("text-xl font-mono font-semibold", s.className)}>
                {s.value}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {s.contribution === "—"
                  ? "—"
                  : s.contribution > 0
                    ? "+1"
                    : s.contribution < 0
                      ? "-1"
                      : "0"}
                {s.note && ` · ${s.note}`}
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        Click a tile to highlight it on the chart · click again to clear.
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        Regime band: ${price(detail.regime_band[0])} – ${price(detail.regime_band[1])} ·
        SMA(50) {detail.sma50 !== null ? `= $${price(detail.sma50)}` : "unknown"}
      </div>
    </section>
  );
}

function signalContrib(
  v: number | null,
  good: number,
  ok: number,
  higherIsBetter: boolean
): number | "—" {
  if (v == null || !Number.isFinite(v)) return "—";
  if (higherIsBetter) {
    if (v >= good) return 1;
    if (v < ok) return -1;
    return 0;
  }
  if (v <= good) return 1;
  if (v > ok) return -1;
  return 0;
}

function RiskCard({ row }: { row: Row }) {
  return (
    <section className="rounded-lg border p-4">
      <h3 className="text-sm font-semibold mb-3">Risk</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCell label="stop" value={`$${price(row.stop_loss)}`} />
        <MetricCell
          label="risk %"
          value={percent(row.risk_pct, 1)}
          className={riskClass(row.risk_pct)}
        />
        <MetricCell
          label="R:R"
          value={ratio(row.reward_risk)}
          className={rrClass(row.reward_risk)}
        />
        <MetricCell label="trail" value={`$${price(row.trail_distance)}`} />
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        Stop = max(support − 1×ATR, entry − 2×ATR) — picks the tighter leg.
      </div>
    </section>
  );
}

// QualityFlagsCard renders the list of shared quality-flag
// annotations from ranking.EvaluateQualityFlags. The main pipeline
// attaches these to every row (never rejects), so even a clean row
// shows an empty-state message here — a user who sees "No quality
// flags" knows the row passed every gate, not just "nothing was
// checked".
function QualityFlagsCard({ row }: { row: Row }) {
  const flags = row.quality_flags ?? [];
  return (
    <section className="rounded-lg border p-4">
      <h3 className="text-sm font-semibold mb-3">Quality Flags</h3>
      {flags.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No quality flags — passes every shared gate.
        </div>
      ) : (
        <ul className="space-y-2">
          {flags.map((f) => (
            <li
              key={f.code}
              className={cn(
                "rounded border px-3 py-2 text-sm",
                f.severity === "block"
                  ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300"
                  : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
              )}
            >
              <div className="font-mono text-xs uppercase tracking-wide">
                {f.code}{" "}
                <span className="opacity-70">({f.severity})</span>
              </div>
              <div className="mt-0.5">{f.message}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// formatMarketCap renders a compact human number (e.g. "$2.8B",
// "$30M") with a dash for null. Shared between MarketCap, Float
// Shares (as share count), etc.
function formatCountOrDash(v: number | null | undefined): string {
  if (v == null) return "–";
  return dollarCompact(v).replace("$", "");
}

function formatMCapOrDash(v: number | null | undefined): string {
  if (v == null) return "–";
  return dollarCompact(v);
}

// formatDaysToEarnings returns a relative "N day(s)" string for a
// next-earnings ISO timestamp. Past dates return "past"; null
// returns "–".
function formatDaysToEarnings(iso: string | null | undefined): string {
  if (!iso) return "–";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "–";
  const ms = then - Date.now();
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  if (days < 0) return "past";
  if (days === 0) return "today";
  return `${days}d`;
}

// FundamentalsCard surfaces the Yahoo quoteSummary-derived numbers
// (market cap, float, short interest, earnings date) plus the
// trailing-return / gap-count decision-support metrics. All fields
// degrade gracefully to a dash when the corresponding data is
// missing (rate-limited lookup, short history, etc.).
function FundamentalsCard({ row }: { row: Row }) {
  const f = row.fundamentals;
  const hasTrailing =
    row.trailing_return_30 != null ||
    row.trailing_return_60 != null ||
    row.recent_gap_count > 0 ||
    row.price_vs_high_30 != null ||
    row.price_vs_low_30 != null ||
    row.relative_volume != null;
  const hasFundamentals =
    f != null &&
    (f.market_cap != null ||
      f.float_shares != null ||
      f.shares_outstanding != null ||
      f.short_percent_of_float != null ||
      f.next_earnings_date != null);
  if (!hasTrailing && !hasFundamentals) {
    return null;
  }
  return (
    <section className="rounded-lg border p-4">
      <h3 className="text-sm font-semibold mb-3">Fundamentals</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCell label="market cap" value={formatMCapOrDash(f?.market_cap)} />
        <MetricCell label="float" value={formatCountOrDash(f?.float_shares)} />
        <MetricCell
          label="short % float"
          value={f?.short_percent_of_float != null ? percent(f.short_percent_of_float, 1) : "–"}
        />
        <MetricCell
          label="earnings in"
          value={formatDaysToEarnings(f?.next_earnings_date ?? null)}
        />
        <MetricCell
          label="30d return"
          value={row.trailing_return_30 != null ? signedPercent(row.trailing_return_30) : "–"}
        />
        <MetricCell
          label="60d return"
          value={row.trailing_return_60 != null ? signedPercent(row.trailing_return_60) : "–"}
        />
        <MetricCell label="gaps ≥3% (60d)" value={String(row.recent_gap_count)} />
        <MetricCell
          label="price vs 30d hi"
          value={row.price_vs_high_30 != null ? percent(row.price_vs_high_30 - 1, 1) : "–"}
        />
        <MetricCell
          label="rel vol (20d)"
          value={row.relative_volume != null ? `${row.relative_volume.toFixed(2)}×` : "–"}
        />
      </div>
      <div className="text-xs text-muted-foreground mt-3">
        Missing fundamentals leave the thin_float / illiquid_vs_float / earnings_5d gates quiet — missing data is not evidence against.
      </div>
    </section>
  );
}

function SwingList({ detail }: { detail: AnalysisDetail }) {
  if (detail.swings.length === 0) {
    return (
      <section className="rounded-lg border p-4 text-sm text-muted-foreground">
        No swing legs detected in the lookback window.
      </section>
    );
  }
  return (
    <section className="rounded-lg border p-4">
      <h3 className="text-sm font-semibold mb-3">Swing legs</h3>
      <div className="text-xs text-muted-foreground mb-2">
        {detail.swings.length} legs · peaks {detail.peak_indices.length} · troughs{" "}
        {detail.trough_indices.length}
      </div>
      <div className="max-h-64 overflow-auto">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1">dir</th>
              <th className="text-right">start</th>
              <th className="text-right">end</th>
              <th className="text-right">Δ%</th>
              <th className="text-right">bars</th>
            </tr>
          </thead>
          <tbody>
            {detail.swings.map((s, i) => (
              <tr key={i}>
                <td
                  className={cn(
                    "py-0.5",
                    s.direction === "up"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-rose-600 dark:text-rose-400"
                  )}
                >
                  {s.direction === "up" ? "↑" : "↓"}
                </td>
                <td className="text-right">${price(s.start_price)}</td>
                <td className="text-right">${price(s.end_price)}</td>
                <td className="text-right">{percent(s.amplitude_pct, 1)}</td>
                <td className="text-right">{s.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MetricCell({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="rounded border p-3 bg-background">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-xl font-mono font-semibold", className)}>{value}</div>
    </div>
  );
}

// Live-tracker card: rendered below the chart in the detail sheet when
// the server has at least one Snapshot for this ticker. Mirrors the
// row-level live cells but with more headroom: a State pill, the full
// intraday change, a distance gauge, and an HOD/LOD range.
const LIVE_STATE_LABEL: Record<TrackerSnapshot["state"], string> = {
  idle: "idle",
  armed: "ARMED",
  triggered: "TRIGGERED",
  invalidated: "INVALIDATED",
};

const LIVE_STATE_PILL: Record<TrackerSnapshot["state"], string> = {
  idle: "bg-muted text-muted-foreground",
  armed: "bg-amber-500/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/40",
  triggered: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/40",
  invalidated: "bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/40",
};

// formatPollInterval turns a seconds count into the label shown in
// the LiveTrackerCard footer. 60s threshold keeps sub-minute values
// distinguishable ("45 s" vs. "1 min") without lying about precision.
function formatPollInterval(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "5 min";
  if (sec < 60) return `${Math.round(sec)} s`;
  if (sec < 120) return "1 min";
  if (sec % 60 === 0) return `${sec / 60} min`;
  return `${(sec / 60).toFixed(1)} min`;
}

// formatCountdown formats a remaining-seconds value for the "next
// check in ..." indicator. Clamped at zero so a briefly overdue poll
// reads "due now" instead of counting negative.
function formatCountdown(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "due now";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

// useTicker re-renders the caller every `ms` milliseconds for as long
// as the component is mounted. Used by LiveTrackerCard to drive the
// "next check in ..." countdown without threading a timer through
// the rest of the app.
function useTicker(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

function LiveTrackerCard({
  row,
  snapshot,
  pollIntervalSec,
}: {
  row: Row;
  snapshot: TrackerSnapshot | undefined;
  pollIntervalSec: number;
}) {
  const pollLabel = formatPollInterval(pollIntervalSec);
  // Re-render every second so the countdown ticks. 1000 ms is cheap
  // enough for a single open sheet — the detail view is modal, so
  // only one instance of this card is ever mounted.
  const now = useTicker(1000);
  if (!snapshot) {
    return (
      <section className="rounded-lg border p-4 text-sm text-muted-foreground">
        Live tracker: waiting for first intraday poll (up to {pollLabel} after
        analyze).
      </section>
    );
  }
  const { state } = snapshot;
  const changeCls =
    snapshot.change_pct > 0
      ? "text-emerald-600 dark:text-emerald-400"
      : snapshot.change_pct < 0
        ? "text-rose-600 dark:text-rose-400"
        : "text-muted-foreground";
  // Distance-to-support gauge: 0 = at support, 1 = at resistance.
  // Clamp to [0, 1.1] so an over-range ticker still renders something.
  const rangeWidth = row.resistance - row.support;
  const rawPos =
    rangeWidth > 0 ? (snapshot.live_price - row.support) / rangeWidth : 0;
  const gaugePos = Math.max(0, Math.min(1.1, rawPos));
  const updated = new Date(snapshot.updated_at);
  // bar_time is the Yahoo wall-clock timestamp of the last 1-min bar
  // ("price as of"); updated_at is when the server polled. They
  // differ by up to one poll cycle. Go's zero time serializes as
  // "0001-01-01T00:00:00Z" — treat that as "unknown" so legacy cached
  // snapshots without a bar_time don't render 1-AD dates.
  const barTime = snapshot.bar_time ? new Date(snapshot.bar_time) : null;
  const hasBarTime = barTime !== null && barTime.getUTCFullYear() > 1;
  const staleSec = hasBarTime
    ? Math.max(0, Math.round((updated.getTime() - barTime!.getTime()) / 1000))
    : null;
  // Next-poll countdown: the server resets its timer immediately
  // after each poll completes, so the next tick fires approximately
  // updated_at + pollIntervalSec. Off by a few hundred ms in practice
  // because network RTT + classify time lands slightly after the
  // timer reset — close enough for a 1-second countdown display.
  const nextAt = updated.getTime() + pollIntervalSec * 1000;
  const secondsUntilNext = Math.max(0, (nextAt - now) / 1000);
  const countdownLabel = formatCountdown(secondsUntilNext);
  // The server's tracker loop pauses polling outside US equity RTH
  // (see internal/tracker.Session.Run), so a countdown toward the
  // next poll interval would just clamp at "due now" overnight and
  // mislead the reader into thinking we're about to hit Yahoo. When
  // the market is closed, the card instead shows a "market closed"
  // label so the user knows the snapshot is the final one from the
  // previous session and no new check is imminent.
  const marketOpen = isMarketOpen(new Date(now));

  return (
    <section className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold">Live tracker</h3>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-mono font-semibold uppercase tracking-wide",
              LIVE_STATE_PILL[state]
            )}
          >
            {LIVE_STATE_LABEL[state]}
          </span>
        </div>
        <div className="text-xs text-muted-foreground font-mono text-right leading-tight">
          {hasBarTime ? (
            <>
              <div>price {barTime!.toLocaleTimeString()}</div>
              <div>
                checked {updated.toLocaleTimeString()}
                {staleSec !== null && staleSec > 0 && ` (+${staleSec}s)`}
              </div>
            </>
          ) : (
            <div>checked {updated.toLocaleTimeString()}</div>
          )}
          {marketOpen ? (
            <div>next in {countdownLabel}</div>
          ) : (
            <div>market closed</div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCell
          label="live"
          value={`$${price(snapshot.live_price)}`}
          className={changeCls}
        />
        <MetricCell
          label="Δ close"
          value={signedPercent(snapshot.change_pct)}
          className={changeCls}
        />
        <MetricCell
          label="Δ support"
          value={signedPercent(snapshot.pct_from_support)}
        />
        <MetricCell
          label="Δ stop"
          value={signedPercent(snapshot.pct_from_stop)}
        />
      </div>
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>support ${price(row.support)}</span>
          <span>
            HOD ${price(snapshot.high_of_day)} · LOD ${price(snapshot.low_of_day)}
          </span>
          <span>${price(row.resistance)} resist</span>
        </div>
        <div className="relative h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              "absolute top-0 bottom-0 w-1 rounded-full",
              state === "triggered"
                ? "bg-emerald-500"
                : state === "armed"
                  ? "bg-amber-500"
                  : state === "invalidated"
                    ? "bg-rose-500"
                    : "bg-sky-500"
            )}
            style={{ left: `${Math.min(100, gaugePos * 100)}%` }}
          />
        </div>
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {snapshot.reason && (
          <div>
            <span className="font-semibold text-foreground/80">why:</span>{" "}
            {snapshot.reason}
          </div>
        )}
        <div>
          Polled every {pollLabel} ·{" "}
          {snapshot.support_atrs == null
            ? "–"
            : percent(snapshot.support_atrs / 10, 2)}{" "}
          ATR above support · last 1m{" "}
          {snapshot.prev_bar_close.toFixed(2)} →{" "}
          {snapshot.bar_close.toFixed(2)}
        </div>
      </div>
    </section>
  );
}
